import { useState, useRef, useEffect, useMemo } from 'react';
import { HelpCircle } from 'lucide-react';

function twoLineRationale(text: string): { line1: string; line2: string } {
  const t = (text || '').trim();
  if (!t) {
    return {
      line1: 'No narrative rationale is stored for this match yet.',
      line2: 'Scores and skills above are computed from the role-fit engine.',
    };
  }
  const sentences = t.split(/(?<=[.!?])\s+/).filter((s) => s.trim());
  if (sentences.length >= 2) {
    return { line1: sentences[0].trim(), line2: sentences.slice(1).join(' ').trim() };
  }
  const words = t.split(/\s+/);
  if (words.length > 14) {
    const mid = Math.ceil(words.length / 2);
    return { line1: words.slice(0, mid).join(' '), line2: words.slice(mid).join(' ') };
  }
  return { line1: t, line2: '' };
}

type Props = {
  recommendation: string;
  /** Stop card click when toggling */
  stopPropagation?: boolean;
};

/**
 * Phase 7 §2.5: compact popover with two-line rationale (LLM / stored recommendation).
 */
export function WhyMatchPopover({ recommendation, stopPropagation = true }: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const { line1, line2 } = useMemo(() => twoLineRationale(recommendation), [recommendation]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative inline-flex" ref={wrapRef}>
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-semibold transition-colors hover:brightness-95"
        style={{
          borderColor: 'var(--vscode-border-primary)',
          background: 'var(--vscode-bg-elevated)',
          color: 'var(--vscode-text-primary)',
        }}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={(e) => {
          if (stopPropagation) {
            e.preventDefault();
            e.stopPropagation();
          }
          setOpen((v) => !v);
        }}
      >
        <HelpCircle className="h-3 w-3 shrink-0 opacity-80" aria-hidden />
        Why this match?
      </button>
      {open ? (
        <div
          className="absolute left-0 top-full z-50 mt-2 w-[min(100vw-2rem,20rem)] rounded-lg border p-3 shadow-lg"
          role="dialog"
          aria-label="Why this match"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            background: 'var(--vscode-bg-elevated)',
          }}
        >
          <p className="text-xs font-medium leading-relaxed" style={{ color: 'var(--vscode-text-primary)' }}>
            {line1}
          </p>
          {line2 ? (
            <p className="mt-2 text-xs leading-relaxed" style={{ color: 'var(--vscode-text-secondary)' }}>
              {line2}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
